"use client";

import type { FC } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { z } from "zod";
import type { TaxonomyOption } from "cmw-client";
import { Button } from "@/components/button";
import { useDrawer } from "@/components/drawer";
import { Input, NestedMultiCombobox, PhoneInput, Select, Textarea } from "@/components/input";
import { toFilterGroups } from "@/components/directory-filter-bar";
import { TurnstileWidget, useTurnstile } from "@/components/turnstile";
import { useTaxonomy } from "@/hooks/use-taxonomy";
import { useSubmitInquiry } from "../../hooks/use-submit-inquiry";

/** Both lists come from `GET /taxonomy/:fieldType` — the API validates the slugs it sends us back. */
const ISSUES_FIELD_TYPE = "focusAreas";
const SESSION_TYPE_FIELD_TYPE = "inquirySessionType";

const toOption = (value: TaxonomyOption) => ({ value: value.slug, label: value.label });

/** The issues people write in about most — offered as one-tap pills so the dropdown stays optional.
 *  Slugs that the taxonomy no longer carries are dropped rather than rendered as dead pills. */
const COMMON_ISSUE_SLUGS = [
  "anxiety",
  "depression",
  "stress-management",
  "trauma",
  "grief-and-loss",
  "relationship-difficulties",
  "adhd-in-adults",
  "self-esteem-and-confidence",
];

/** Mirrors `CreateInquiryDto` on the API: same limits, so a form that validates is a valid request. */
const contactPractitionerSchema = z.object({
  fullName: z.string().trim().min(1, "Enter your full name").max(160, "Use 160 characters or fewer"),
  /* `PhoneInput` emits E.164 already, so this only has to reject a number RPN judged incomplete. */
  phone: z
    .string()
    .trim()
    .min(1, "Enter a phone number")
    .refine(isValidPhoneNumber, "Enter a valid phone number"),
  email: z
    .string()
    .trim()
    .min(1, "Enter your email")
    .email("Enter a valid email address")
    .max(320, "Use 320 characters or fewer"),
  issues: z.array(z.string()).min(1, "Select at least one issue"),
  sessionType: z.string().min(1, "Select a preferred session type"),
  comment: z.string().trim().max(2000, "Use 2000 characters or fewer").optional(),
});

type ContactPractitionerValues = z.infer<typeof contactPractitionerSchema>;

const DEFAULT_VALUES: ContactPractitionerValues = {
  fullName: "",
  phone: "",
  email: "",
  issues: [],
  sessionType: "",
  comment: "",
};

type ContactPractitionerFormProps = {
  /** Who the inquiry is addressed to — the practitioner's public username, not their internal id. */
  username: string;
  /** Shown on the `/inquiry-sent` confirmation page once the request goes through. */
  practitionerName: string;
};

/** Drawer content opened from the "Contact Me" button on a practitioner card (Figma 854:37883).
 *  The shell — title, close glyph, top rule — is drawn by the drawer itself (spec: drawer.tsx). */
export const ContactPractitionerForm: FC<ContactPractitionerFormProps> = ({ username, practitionerName }) => {
  const { closeDrawer } = useDrawer();
  const { turnstileRef, takeTurnstileToken } = useTurnstile();
  const submitInquiry = useSubmitInquiry(username, practitionerName, takeTurnstileToken);
  const { getAll, isLoading } = useTaxonomy();
  const taxonomies = getAll();
  const issueOptions = taxonomies.find((t) => t.fieldType === ISSUES_FIELD_TYPE)?.options ?? [];
  const sessionTypeOptions =
    taxonomies.find((t) => t.fieldType === SESSION_TYPE_FIELD_TYPE)?.options ?? [];
  const { control, handleSubmit } = useForm<ContactPractitionerValues>({
    resolver: zodResolver(contactPractitionerSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const onSubmit = ({ comment, ...values }: ContactPractitionerValues) =>
    /* An empty comment is left off the body rather than sent as "" — the field is optional. */
    submitInquiry.mutate({ ...values, ...(comment ? { comment } : {}) });

  return (
    /* `noValidate`: the fields keep `required` for the red asterisk and screen readers, but the
       browser's own bubble would block submit before zod ran — these messages are ours to show. */
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex min-h-full flex-col gap-24">
      <div className="flex flex-col gap-24">
        <Controller
          control={control}
          name="fullName"
          render={({ field, fieldState }) => (
            <Input
              id="fullName"
              name={field.name}
              required
              label="Full Name"
              placeholder="Please input"
              value={field.value}
              onChange={field.onChange}
              state={fieldState.error ? "error" : "default"}
              message={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="phone"
          render={({ field, fieldState }) => (
            <PhoneInput
              id="phone"
              name={field.name}
              required
              label="Phone for Enquiries"
              placeholder="Please input"
              value={field.value}
              onChange={field.onChange}
              state={fieldState.error ? "error" : "default"}
              message={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => (
            <Input
              id="email"
              name={field.name}
              type="email"
              required
              label="Contact Email for Enquiries"
              placeholder="johndoe@gmail.com"
              value={field.value}
              onChange={field.onChange}
              state={fieldState.error ? "error" : "default"}
              message={fieldState.error?.message}
            />
          )}
        />

        <hr className="border-neutral-light-grey" />

        <Controller
          control={control}
          name="issues"
          render={({ field, fieldState }) => (
            <NestedMultiCombobox
              id="issues"
              required
              label="Issue"
              placeholder={isLoading ? "Loading..." : "Please select"}
              groups={toFilterGroups(issueOptions)}
              shortcuts={COMMON_ISSUE_SLUGS}
              value={field.value}
              onValueChange={field.onChange}
              state={fieldState.error ? "error" : "default"}
              message={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="sessionType"
          render={({ field, fieldState }) => (
            <Select
              id="sessionType"
              required
              label="Preferred Session Types"
              placeholder={isLoading ? "Loading..." : "Please select"}
              options={sessionTypeOptions.map(toOption)}
              value={field.value}
              onValueChange={field.onChange}
              state={fieldState.error ? "error" : "default"}
              message={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="comment"
          render={({ field }) => (
            <Textarea
              id="comment"
              name={field.name}
              label="Comment"
              placeholder="Please input"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />

        <TurnstileWidget ref={turnstileRef} action="inquiry" />
      </div>

      <div className="mt-auto flex flex-col gap-16 pt-16">
        <hr className="border-neutral-light-grey" />
        <div className="flex gap-24">
          <Button
            type="button"
            variant="secondary-white"
            size="l"
            className="flex-1"
            onClick={closeDrawer}
            disabled={submitInquiry.isPending}
          >
            Back
          </Button>
          <Button type="submit" size="l" className="flex-1" disabled={submitInquiry.isPending}>
            {submitInquiry.isPending ? "Sending..." : "Submit Request"}
          </Button>
        </div>
      </div>
    </form>
  );
};
